import React, { Children, cloneElement, forwardRef, isValidElement } from 'react';

const getTextContent = (node) => {
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getTextContent).join(' ');
    if (isValidElement(node)) return getTextContent(node.props.children);
    return '';
};

const isSerialHeader = (header) => /^(?:#|no\.?|sl(?:\.?\s*no\.?)?|sr\.?\s*no\.?|serial\s*no\.?|s\.?\s*no\.?)$/i.test(header.trim());
const SERIAL_COLUMN_STYLE = {
    width: '60px',
    minWidth: '60px',
    maxWidth: '60px',
    boxSizing: 'border-box',
    textAlign: 'center',
    fontSize: '0.8125rem',
    fontWeight: 700,
};
const HEADER_CELL_STYLE = {
    backgroundColor: '#f8f9fa',
    color: '#64748b',
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: 'normal',
    textTransform: 'none',
};

const withSerialWidth = (cell, children = cell.props.children) => cloneElement(cell, {
    style: { ...cell.props.style, ...SERIAL_COLUMN_STYLE },
}, children);

const sectionKind = (section) => (
    typeof section.type === 'string' ? section.type : section.type?.tableSection
);

const getRows = (section) => Children.toArray(section.props.children).filter(
    (child) => isValidElement(child) && child.type === 'tr'
);

const normalizeHeaderStyle = (section) => cloneElement(section, {}, Children.map(section.props.children, (row) => {
    if (!isValidElement(row) || row.type !== 'tr') return row;
    return cloneElement(row, {}, Children.map(row.props.children, (cell) => (
        isValidElement(cell) && (cell.type === 'th' || cell.type === 'td')
            ? cloneElement(cell, { style: { ...cell.props.style, ...HEADER_CELL_STYLE } })
            : cell
    )));
}));

const addSerialHeader = (section) => {
    const rows = getRows(section);
    if (!rows.length) return section;

    const cells = Children.toArray(rows[0].props.children);
    const hasSerialCell = cells.some((cell) => (
        isValidElement(cell)
        && (cell.type === 'th' || cell.type === 'td')
        && isSerialHeader(getTextContent(cell.props.children))
    ));
    if (hasSerialCell) return section;

    const serialCell = <th key="table-serial-header" scope="col" style={SERIAL_COLUMN_STYLE} className="tw-whitespace-nowrap">SL No.</th>;
    let updatedFirstRow = false;
    const updatedRows = Children.map(section.props.children, (row) => {
        if (!isValidElement(row) || row.type !== 'tr' || updatedFirstRow) return row;
        updatedFirstRow = true;
        return cloneElement(row, {}, [serialCell, ...cells]);
    });

    return cloneElement(section, {}, updatedRows);
};

const addSerialCells = (section, serialStart) => {
    let rowNumber = serialStart - 1;
    const updatedRows = Children.map(section.props.children, (row) => {
        if (!isValidElement(row) || row.type !== 'tr') return row;

        const cells = Children.toArray(row.props.children);
        if (cells.some((cell) => isValidElement(cell) && cell.props?.['data-table-serial'] === 'true')) return row;

        const hasColSpanOnly = cells.length === 1
            && isValidElement(cells[0])
            && (cells[0].type === 'td' || cells[0].type === 'th')
            && Number(cells[0].props.colSpan || 1) > 1;

        if (hasColSpanOnly) {
            const serialCell = <td key="table-serial-cell" data-table-serial="true" style={SERIAL_COLUMN_STYLE} className="tw-text-center" />;
            return cloneElement(row, {}, [serialCell, ...cells]);
        }

        rowNumber += 1;
        const serialCell = <td key="table-serial-cell" data-table-serial="true" style={SERIAL_COLUMN_STYLE} className="tw-text-center">{rowNumber}</td>;
        return cloneElement(row, {}, [serialCell, ...cells]);
    });

    return cloneElement(section, {}, updatedRows);
};

const normalizeSerialHeader = (section, columnIndex) => {
    let firstRow = true;
    const updatedRows = Children.map(section.props.children, (row) => {
        if (!isValidElement(row) || row.type !== 'tr' || !firstRow) return row;
        firstRow = false;
        const cells = Children.toArray(row.props.children);
        return cloneElement(row, {}, cells.map((cell, index) => (
            index === columnIndex && isValidElement(cell) ? withSerialWidth(cell, 'SL No.') : cell
        )));
    });
    return cloneElement(section, {}, updatedRows);
};

const normalizeSerialBody = (section, columnIndex) => {
    const updatedRows = Children.map(section.props.children, (row) => {
        if (!isValidElement(row) || row.type !== 'tr') return row;
        const cells = Children.toArray(row.props.children);
        if (cells.length === 1 && Number(cells[0]?.props?.colSpan || 1) > 1) return row;
        return cloneElement(row, {}, cells.map((cell, index) => (
            index === columnIndex && isValidElement(cell) ? withSerialWidth(cell) : cell
        )));
    });
    return cloneElement(section, {}, updatedRows);
};

const NumberedTable = forwardRef(function NumberedTable({ children, serialNumbers = 'auto', serialStart = 1, ...props }, ref) {
    const childrenArray = Children.toArray(children);
    const hasHeader = childrenArray.some((section) => isValidElement(section) && sectionKind(section) === 'thead');
    const headerSection = childrenArray.find((section) => isValidElement(section) && sectionKind(section) === 'thead');
    const headerCells = headerSection && getRows(headerSection)[0]
        ? Children.toArray(getRows(headerSection)[0].props.children)
        : [];
    const serialColumnIndex = headerCells.findIndex((cell) => (
        isValidElement(cell)
        && (cell.type === 'th' || cell.type === 'td')
        && isSerialHeader(getTextContent(cell.props.children))
    ));

    const numberedChildren = serialNumbers === false || !hasHeader ? children : Children.map(children, (section) => {
        if (!isValidElement(section)) return section;
        if (serialColumnIndex >= 0) {
            if (sectionKind(section) === 'thead') return normalizeSerialHeader(section, serialColumnIndex);
            if (sectionKind(section) === 'tbody') return normalizeSerialBody(section, serialColumnIndex);
            return section;
        }
        if (sectionKind(section) === 'thead') return addSerialHeader(section);
        if (sectionKind(section) === 'tbody') return addSerialCells(section, serialStart);
        return section;
    });

    const styledChildren = Children.map(numberedChildren, (section) => (
        isValidElement(section) && sectionKind(section) === 'thead'
            ? normalizeHeaderStyle(section)
            : section
    ));

    return <table ref={ref} {...props}>{styledChildren}</table>;
});

export default NumberedTable;
